import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { scrollToSection } from "@/App";

const LINES = ["ROOF", "CARPENTRY &", "TIMBER FRAMING"];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-32">
        <img
          src="https://customer-assets-rejwkqb3.emergentagent.net/job_perth-roof-carpentry/artifacts/5jqhp2yi_869216b4-9aa5-4125-b34d-874f70d7cfbc.jpg"
          alt="New Perth home with A E Carpentry roof trusses installed"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-black/40" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 pt-40 w-full">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          data-testid="hero-overline"
          className="text-sm uppercase tracking-[0.3em] text-timber mb-6"
        >
          A E Carpentry — Perth, WA
        </motion.p>

        <h1 data-testid="hero-heading" className="font-display font-black uppercase leading-[0.85] tracking-tighter text-6xl sm:text-7xl lg:text-8xl xl:text-9xl text-white">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {i === 2 ? (
                  <>
                    <span className="text-outline-timber">TIMBER</span> FRAMING
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-10 flex flex-col md:flex-row md:items-end gap-8 md:gap-16"
        >
          <p data-testid="hero-subtext" className="text-base md:text-lg text-neutral-300 max-w-md leading-relaxed">
            Building or renovating? Get your roof structure sorted quickly and
            professionally — stick roofing, trusses, framing and structural
            timber, Perth-wide.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              data-testid="hero-quote-cta"
              onClick={() => scrollToSection("#quote")}
              className="group bg-timber hover:bg-timber-hover text-obsidian font-display font-bold uppercase tracking-widest px-8 py-4 text-base transition-colors duration-300 flex items-center gap-3"
            >
              Free Measure &amp; Quote
              <ArrowDown size={18} className="transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <a
              data-testid="hero-call-cta"
              href="tel:0408166415"
              className="border border-white/30 hover:border-timber hover:text-timber text-white font-display font-bold uppercase tracking-widest px-8 py-4 text-base transition-colors duration-300 flex items-center gap-3"
            >
              <Phone size={18} /> 0408 166 415
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
